import { useLayoutEffect, useReducer, useRef } from "react";
import { useIntl } from "react-intl";
import { WmButton } from "@watermarkinsights/ripple-react";
import ReactMarkdown from "react-markdown";

const DISALLOWED_MARKDOWN_ELEMENTS = [
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "strong",
  "em",
  "ul",
  "ol",
  "li",
  "blockquote",
];

interface DraftCardPreviewProps {
  response: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

function DraftCardPreview({ response, isExpanded, onToggleExpand }: DraftCardPreviewProps) {
  const intl = useIntl();
  const previewRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setOverflowing] = useReducer(
    (_: boolean, next: boolean) => next,
    false,
  );

  useLayoutEffect(() => {
    const element = previewRef.current;
    if (!element || isExpanded) return;

    const measure = () => setOverflowing(element.scrollHeight > element.clientHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [response, isExpanded]);

  return (
    <>
      <div
        ref={previewRef}
        className={`dcp__card-preview${isExpanded ? " dcp__card-preview--expanded" : ""}`}
      >
        <ReactMarkdown
          disallowedElements={DISALLOWED_MARKDOWN_ELEMENTS}
          unwrapDisallowed
        >
          {response}
        </ReactMarkdown>
      </div>
      {isOverflowing && (
        <WmButton
          buttonType="textonly"
          className="dcp__card-toggle"
          onClick={onToggleExpand}
        >
          {intl.formatMessage({
            id: isExpanded
              ? "app.toolboxChat.draftConversation.hideFullResponse"
              : "app.toolboxChat.draftConversation.showFullResponse",
          })}
        </WmButton>
      )}
    </>
  );
}

export default DraftCardPreview;
